/** SEO 描述留空時自動摘要的長度上限（Google 搜尋結果約顯示 80～120 個中文字）。 */
export const AUTO_DESCRIPTION_MAX = 120;

interface PMNode {
  type?: string;
  text?: string;
  content?: PMNode[];
}

const squash = (s: string) => s.replace(/\s+/g, ' ').trim();

/** 新聞稿開頭的署名：【記者 辛澎祥/採訪報導】、【特約撰述/黃調元】、記者 王小明／台北報導… */
const BYLINE_PREFIX = /^【[^】]{0,30}】\s*/;
const BYLINE_SUFFIX = /\s*【[^】]{0,30}(報導|撰述|攝影)】$/;
const BYLINE_LINE = /^(記者|特約|撰文|文字?|圖|攝影|編輯|採訪)[^。！？]{0,30}(報導|撰述|攝影|整理)?$/;

/** 短於此字數的段落視為標籤／署名（如「藝文」），優先找更長的第一段。 */
const MIN_LEAD = 20;

/** 第一個夠長的段落；都很短就退回第一個非空的。 */
function pickLead(paras: Iterable<string>): string {
  let fallback = '';
  for (const t of paras) {
    if (!t) continue;
    if (Array.from(t).length >= MIN_LEAD) return t;
    fallback ||= t;
  }
  return fallback;
}

/** 去掉開頭（及舊文常見的結尾）署名；整段只是署名就回空字串（跳過這段）。 */
function stripByline(t: string): string {
  const rest = t.replace(BYLINE_PREFIX, '').replace(BYLINE_SUFFIX, '').trim();
  return BYLINE_LINE.test(rest) && Array.from(rest).length <= 30 ? '' : rest;
}

function nodeText(n: PMNode): string {
  if (n.type === 'text') return n.text ?? '';
  if (n.type === 'hardBreak') return ' ';
  return (n.content ?? []).map(nodeText).join('');
}

/** Tiptap JSON 依序走訪的段落文字（已去署名）。 */
function* jsonParagraphs(json: string): Generator<string> {
  let root: PMNode;
  try {
    root = JSON.parse(json) as PMNode;
  } catch {
    return; // 壞掉的 JSON：當作沒有
  }
  const stack: PMNode[] = [root];
  while (stack.length) {
    const n = stack.shift()!;
    if (n.type === 'paragraph') yield stripByline(squash(nodeText(n)));
    else if (n.content) stack.unshift(...n.content);
  }
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", nbsp: ' ' };

/** 舊站 HTML 依序的 <p> 文字（去標籤、解 entity、去署名）。 */
function* htmlParagraphs(html: string): Generator<string> {
  for (const m of html.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)) {
    yield stripByline(
      squash(
        m[1]!
          .replace(/<br\s*\/?>/gi, ' ')
          .replace(/<[^>]+>/g, '')
          .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (_, e: string) => ENTITIES[e]!),
      ),
    );
  }
}

/**
 * 內文第一段（跳過開頭的記者署名、分類標籤等短行）的純文字，超過 `max` 字截斷加「…」。給 SEO 描述留空時當預設值。
 * 優先 Tiptap JSON，沒有才用遷移保留的舊 HTML；都沒有回空字串。
 */
export function autoDescription(
  bodyJson: string | null | undefined,
  legacyHtml?: string | null,
  max = AUTO_DESCRIPTION_MAX,
): string {
  const text = (bodyJson && pickLead(jsonParagraphs(bodyJson))) || (legacyHtml && pickLead(htmlParagraphs(legacyHtml))) || '';
  const chars = Array.from(text);
  return chars.length > max ? `${chars.slice(0, max).join('')}…` : text;
}
